import { useRef, forwardRef, useImperativeHandle } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logo from '../../assets/images/logokiota.png';
import '../../styles/receipt-print.css'; // Importer les styles d'impression
import { Invoice } from '../../types/profile';

interface ReceiptProps {
  payment: {
    id: string;
    date: string;
    amount: number;
    method: string;
    plan: string;
    status: string;
    receiptUrl?: string;
  };
  /** Données réelles de la facture backend. Quand fourni, prend la priorité sur payment. */
  invoice?: Invoice;
}

export interface ReceiptRef {
  downloadPDF: () => void;
}

const Receipt = forwardRef<ReceiptRef, ReceiptProps>(({ payment, invoice }, ref) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  // ===== Données de référence =====
  // Priorité : invoice (backend) > payment (local)
  const refNumber   = invoice?.invoiceNumber ?? payment.id;
  const displayDate = invoice?.issueDate ?? invoice?.paidDate ?? payment.date;
  const totalTTC    = invoice?.amount ?? payment.amount;
  const currency    = invoice?.currency ?? 'USD';
  const payMethod   = payment.method;
  const payStatus   = payment.status;

  // Description / période de la facture
  let description: string;
  if (invoice?.billingPeriod) {
    // Format "2026-03-01/2026-04-01" → "01/03/2026 – 01/04/2026"
    const parts = invoice.billingPeriod.split('/');
    if (parts.length === 2) {
      const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      description = `${invoice.planName ?? payment.plan} — Période : ${fmt(parts[0])} au ${fmt(parts[1])}`;
    } else {
      description = invoice.planName ?? payment.plan;
    }
  } else if (/recharge|token/i.test(invoice?.planName ?? payment.plan)) {
    description = 'Rechargement de tokens';
  } else {
    const d = new Date(displayDate);
    const mois = d.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
    // Détection abonnement annuel : si le montant > 3× le prix mensuel moyen, c'est annuel
    description = `${invoice?.planName ?? payment.plan} — ${mois}`;
  }

  // Notes du backend
  const notes = invoice?.notes ?? null;

  // Lignes de facturation
  // Si invoice.items[] est disponible, utiliser le détail réel
  // Sinon, construire une seule ligne avec le total payment
  const lineItems: Array<{ label: string; qty: number; unitPrice: number; amount: number }> =
    invoice?.items && invoice.items.length > 0
      ? invoice.items.map(item => ({
          label: item.description,
          qty: item.quantity,
          unitPrice: item.unitPrice,
          amount: item.amount,
        }))
      : [{ label: invoice?.planName ?? payment.plan, qty: 1, unitPrice: totalTTC, amount: totalTTC }];

  // TVA RDC 16% (OHADA) — calculée sur le TTC total
  const montantHT = totalTTC / 1.16;
  const tva       = totalTTC - montantHT;

  // ===== Infos client — priorité billingAddress backend =====
  let clientName    = 'Nom de la société';
  let userName      = '';
  let clientAddress = invoice?.billingAddress ?? '';
  let clientPhone   = '';

  try {
    const stored = localStorage.getItem('auth0_user');
    if (stored) {
      const user = JSON.parse(stored);
      if (user.company || user.organization || user.org) {
        clientName = user.company || user.organization || user.org;
      }
      if (user.name) userName = user.name;
      if (!clientAddress && user.address) clientAddress = user.address;
      if (user.phone) clientPhone = user.phone;
    }
  } catch {
    // Profil local illisible : le reçu garde les coordonnées par défaut.
  }

  function formatDate(date: string) {
    return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  useImperativeHandle(ref, () => ({
    downloadPDF: handleDownloadPDF
  }));

  function handleDownloadPDF() {
    const input = receiptRef.current;
    if (!input) return;

    html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
    }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth  = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const ratio  = canvas.width / canvas.height;
        const width  = pdfWidth;
        const height = Math.min(width / ratio, pdfHeight);

        pdf.addImage(imgData, 'PNG', 0, 0, width, height);
        pdf.save(`facture-${refNumber}.pdf`);
    });
  }

  return (
    <div ref={receiptRef} className="receipt-container bg-white text-gray-900 p-6 sm:p-10 w-full font-sans">
        {/* En-tête */}
        <div className="receipt-header pb-6 border-b border-gray-200">
            <div className="text-center mb-4">
                <img src={logo} alt="i-kiotahub" className="receipt-logo h-16 mx-auto" />
            </div>
            <h2 className="font-bold text-3xl text-gray-800 text-center">FACTURE</h2>
            <div className="flex justify-between items-start text-sm text-gray-600 mt-4">
                <div className="font-semibold text-base">Réf. {refNumber}</div>
                <div>Date : {formatDate(displayDate)}</div>
            </div>
        </div>
        
        {/* Infos client et vendeur */}
        <div className="receipt-parties grid grid-cols-2 gap-8 my-8 text-sm">
            <div>
                <div className="font-semibold text-gray-500 mb-2 uppercase tracking-wider">Fournisseur</div>
                <div className="font-bold text-gray-800 text-base">i-kiotahub</div>
                <div className="text-gray-600 mt-1">
                    <div>RCCM: CD/GOMA/RCCM/23-B-00196</div>
                    <div>ID NAT: 19-H5300-N40995F</div>
                    <div>NIF: A2321658S</div>
                    <div>Email: ikiota@ikiotahub.com</div>
                    <div>Tél: +243 979 588 462</div>
                </div>
            </div>
            <div className="text-right">
                <div className="font-semibold text-gray-500 mb-2 uppercase tracking-wider">Client</div>
                <div className="font-bold text-gray-800 text-base">{clientName}</div>
                <div className="text-gray-600 mt-1">
                    {userName && <div>Payé par : {userName}</div>}
                    {clientAddress && <div>Adresse : {clientAddress}</div>}
                    {clientPhone && <div>Tél : {clientPhone}</div>}
                </div>
            </div>
        </div>
        
        {/* Description paiement */}
        <div className="receipt-description mb-6">
            <h3 className="text-base font-bold text-primary uppercase tracking-wide">{description}</h3>
            {notes && <p className="text-sm text-gray-600 mt-1 italic">{notes}</p>}
        </div>
        
        {/* Tableau des lignes — données réelles du backend */}
        <div className="receipt-table mb-8 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="p-3 text-left font-semibold text-gray-600 border-b">Libellé</th>
                        <th className="p-3 text-right font-semibold text-gray-600 border-b">PU HT</th>
                        <th className="p-3 text-right font-semibold text-gray-600 border-b">Qté</th>
                        <th className="p-3 text-right font-semibold text-gray-600 border-b">Montant HT</th>
                    </tr>
                </thead>
                <tbody>
                    {lineItems.map((item, i) => {
                      const itemHT = item.amount / 1.16;
                      return (
                        <tr key={i} className="bg-white">
                          <td className="p-3 border-b">{item.label}</td>
                          <td className="p-3 text-right border-b">{(item.unitPrice / 1.16).toFixed(2)} {currency}</td>
                          <td className="p-3 text-right border-b">{item.qty}</td>
                          <td className="p-3 text-right border-b">{itemHT.toFixed(2)} {currency}</td>
                        </tr>
                      );
                    })}
                </tbody>
            </table>
        </div>
        
        {/* Récapitulatif */}
        <div className="receipt-summary flex justify-end mb-8">
            <div className="w-full max-w-sm text-sm">
                <div className="flex justify-between py-2">
                    <span className="text-gray-600">Total HT</span>
                    <span className="font-semibold text-gray-800">{montantHT.toFixed(2)} {currency}</span>
                </div>
                <div className="flex justify-between py-2">
                    <span className="text-gray-600">TVA (16%)</span>
                    <span className="font-semibold text-gray-800">{tva.toFixed(2)} {currency}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-gray-300 mt-2">
                    <span className="font-bold text-gray-900 text-base">Total TTC</span>
                    <span className="font-bold text-primary text-xl">{totalTTC.toFixed(2)} {currency}</span>
                </div>
            </div>
        </div>
        
        {/* Infos paiement */}
        <div className="receipt-payment-info text-sm p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="mb-1"><strong>Méthode de paiement :</strong> <span className="text-gray-800">{payMethod}</span></div>
            <div><strong>Statut :</strong> <span className="font-semibold text-green-600">{payStatus}</span></div>
        </div>
        
        {/* Footer OHADA */}
        <div className="receipt-footer text-xs text-gray-500 border-t border-gray-200 pt-4 mt-8 text-center">
            <p className="receipt-address mt-2"><strong>Adresse :</strong> RDC/Nord-Kivu/Goma, Commune de Goma, Quartier Lac Vert, Avenue Kabanda</p>
        </div>
    </div>
  );
});

export default Receipt;
